import { HttpClient, HttpContext, HttpHeaders } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { map } from "rxjs";
import {
  CollectionOfArgument,
  CollectionOfInclude,
  DefaultTechData,
  DefaultTechDataCategory,
} from "./generated";
import { ConfigService } from "../config/config.service";

@Injectable({
  providedIn: "root",
})
export class PiaProductDetailService {
  configService = inject(ConfigService);
  http = inject(HttpClient);

  getArgumentsByContext(
    {
      countryID,
      languageID,
      year,
    }: { countryID: string; languageID: string; year: number },
    context?: HttpContext,
  ) {
    const url = `${this.configService.piaAPIOrigin}/Year(ID=${encodeURIComponent(year.toString())},Country='${encodeURIComponent(countryID)}',Language='${encodeURIComponent(languageID)}')/Arguments`;

    const headers = new HttpHeaders({
      accept: "application/json",
    });

    return this.http.get<{ d?: CollectionOfArgument }>(url, {
      headers,
      context,
    });
  }

  getProductExcludesByContextAndProductId(
    {
      countryID,
      languageID,
      year,
      productID,
    }: {
      countryID: string;
      languageID: string;
      year: number;
      productID: string;
    },
    context?: HttpContext,
  ) {
    const url = `${this.configService.piaAPIOrigin}/Exclude?$filter=Country eq '${encodeURIComponent(countryID)}' and Language eq '${encodeURIComponent(languageID)}' and year eq ${encodeURIComponent(year.toString())} and ProductId eq '${encodeURIComponent(productID)}'`;

    const headers = new HttpHeaders({
      accept: "application/json",
    });

    return this.http.get<{ d?: CollectionOfInclude }>(url, {
      headers,
      context,
    });
  }

  getProductIncludesByContextAndProductId(
    {
      countryID,
      languageID,
      year,
      productID,
    }: {
      countryID: string;
      languageID: string;
      year: number;
      productID: string;
    },
    context?: HttpContext,
  ) {
    const url = `${this.configService.piaAPIOrigin}/Include?$filter=Country eq '${encodeURIComponent(countryID)}' and Language eq '${encodeURIComponent(languageID)}' and year eq ${encodeURIComponent(year.toString())} and ProductId eq '${encodeURIComponent(productID)}'`;

    const headers = new HttpHeaders({
      accept: "application/json",
    });

    return this.http.get<{ d?: CollectionOfInclude }>(url, {
      headers,
      context,
    });
  }

  getTechDataCategoriesByYearAndCountryAndLanguage(
    {
      countryID,
      languageID,
      year,
    }: {
      countryID: string;
      languageID: string;
      year: number;
    },
    context?: HttpContext,
  ) {
    const url = `${this.configService.piaAPIOrigin}/TechDataCategory?$filter=Year eq ${encodeURIComponent(year.toString())} and Country eq '${encodeURIComponent(countryID)}' and Language eq '${encodeURIComponent(languageID)}'`;

    const headers = new HttpHeaders({
      accept: "application/json",
    });

    return this.http.get<{ d?: { results: DefaultTechDataCategory[] } }>(url, {
      headers,
      context,
    });
  }

  getAllTechDataByYearAndCountryAndLanguage(
    {
      countryID,
      languageID,
      year,
    }: {
      countryID: string;
      languageID: string;
      year: number;
    },
    context?: HttpContext,
  ) {
    const url = `${this.configService.piaAPIOrigin}/TechData?$filter=Year eq ${encodeURIComponent(year.toString())} and Country eq '${encodeURIComponent(countryID)}' and Language eq '${encodeURIComponent(languageID)}'`;

    const headers = new HttpHeaders({
      accept: "application/json",
    });

    return this.http.get<{ d?: { results: DefaultTechData[] } }>(url, {
      headers,
      context,
    });
  }

  getProductTechDataByProductId(
    {
      countryID,
      languageID,
      year,
      productID,
    }: {
      countryID: string;
      languageID: string;
      year: number;
      productID: string;
    },
    context?: HttpContext,
  ) {
    const url = `${this.configService.piaAPIOrigin}/Product(ID='${encodeURIComponent(productID)}',Year=${encodeURIComponent(year.toString())},Language='${encodeURIComponent(languageID)}',Country='${encodeURIComponent(countryID)}')/TechData?$expand=Category`;

    const headers = new HttpHeaders({
      accept: "application/json",
    });

    return this.http
      .get<{ d?: { results: DefaultTechData[] } }>(url, { headers, context })
      .pipe(
        map((response) => {
          const allTechData = response?.d?.results;
          const productTechData = allTechData?.filter(
            (techDataEntry) => techDataEntry.Product === productID,
          );
          return productTechData;
        }),
      );
  }
}
